import { motion } from "framer-motion";
import { Check, ArrowUpRight, Monitor, Smartphone, Layers, Play, Download } from "lucide-react";
import { useLang } from "./LanguageContext";
import { Reveal } from "./Reveal";
import { useAutoCarousel } from "./useAutoCarousel";
import { APP_IMAGES, TRX_URL, PLAYSTORE_URL, APK_URL } from "./content";

const ICONS = [Monitor, Smartphone, Layers];

export const AppShowcase = () => {
  const { t } = useLang();
  const images = [APP_IMAGES.desktop, APP_IMAGES.mobile, APP_IMAGES.combo];
  const { emblaRef, selected, scrollTo } = useAutoCarousel(4500, { align: "center" });

  return (
    <section className="relative py-24 md:py-32 border-t border-white/10 overflow-hidden">
      <div className="pointer-events-none absolute top-1/3 left-1/2 -translate-x-1/2 h-[420px] w-[620px] rounded-full bg-[#ccff00]/10 blur-[150px]" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        {/* Copy */}
        <div>
          <Reveal>
            <span className="text-xs font-bold tracking-[0.25em] uppercase text-[#ccff00]">
              {t.showcase.overline}
            </span>
            <h2 className="font-display font-bold text-3xl md:text-5xl tracking-tight text-white mt-4 leading-tight">
              {t.showcase.title}
            </h2>
            <p className="mt-5 text-zinc-400 font-light text-base md:text-lg leading-relaxed max-w-lg">
              {t.showcase.desc}
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <ul className="mt-8 grid sm:grid-cols-2 gap-4">
              {t.showcase.points.map((p, i) => (
                <li key={i} data-testid={`showcase-point-${i}`} className="flex items-center gap-3">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center bg-[#ccff00] text-black">
                    <Check className="h-4 w-4" />
                  </span>
                  <span className="text-sm md:text-base text-zinc-300">{p}</span>
                </li>
              ))}
            </ul>
            <a
              href={TRX_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="showcase-cta-btn"
              className="group mt-10 inline-flex items-center gap-2 h-13 px-7 py-4 bg-[#ccff00] text-black font-bold text-sm hover:bg-[#b3e600] transition-colors"
            >
              {t.cta.start}
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>

            <div className="mt-10 pt-8 border-t border-white/10">
              <span className="text-xs font-bold tracking-[0.25em] uppercase text-zinc-500">
                {t.showcase.download.title}
              </span>
              <div className="mt-4 flex flex-wrap gap-3">
                <a
                  href={PLAYSTORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="download-play-btn"
                  className="group inline-flex items-center gap-3 h-14 px-5 border border-white/15 bg-white/[0.02] hover:border-[#ccff00] transition-colors"
                >
                  <Play className="h-6 w-6 text-[#ccff00] fill-[#ccff00]" />
                  <span className="leading-tight text-left">
                    <span className="block text-[10px] text-zinc-500 uppercase tracking-wider">Get it on</span>
                    <span className="block font-display font-semibold text-white">{t.showcase.download.play}</span>
                  </span>
                </a>
                <a
                  href={APK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="download-apk-btn"
                  className="group inline-flex items-center gap-3 h-14 px-5 border border-white/15 bg-white/[0.02] hover:border-[#ccff00] transition-colors"
                >
                  <Download className="h-6 w-6 text-[#ccff00]" />
                  <span className="leading-tight text-left">
                    <span className="block text-[10px] text-zinc-500 uppercase tracking-wider">Android</span>
                    <span className="block font-display font-semibold text-white">{t.showcase.download.apk}</span>
                  </span>
                </a>
              </div>
              <p className="mt-3 text-xs text-zinc-500">{t.showcase.download.note}</p>
            </div>
          </Reveal>
        </div>

        {/* Slider */}
        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          data-testid="app-slider"
        >
          <div className="relative border border-white/10 bg-white/[0.02] p-2">
            <div className="overflow-hidden" ref={emblaRef}>
              <div className="flex">
                {images.map((src, i) => (
                  <div key={i} className="min-w-0 shrink-0 grow-0 basis-full">
                    <div className="relative aspect-[16/11] bg-black overflow-hidden">
                      <img
                        src={src}
                        alt={t.showcase.slides[i].title}
                        className="h-full w-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-6">
                        <div className="flex items-center gap-2 text-[#ccff00]">
                          {(() => {
                            const Icon = ICONS[i];
                            return <Icon className="h-5 w-5" />;
                          })()}
                          <span className="font-display font-semibold text-lg text-white">
                            {t.showcase.slides[i].title}
                          </span>
                        </div>
                        <p className="mt-1 text-sm text-zinc-300 font-light">{t.showcase.slides[i].desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-5 flex items-center gap-2">
            {images.map((_, i) => (
              <button
                key={i}
                data-testid={`app-slider-dot-${i}`}
                onClick={() => scrollTo(i)}
                aria-label={`Slide ${i + 1}`}
                className={`h-1.5 transition-all duration-300 ${
                  selected === i ? "w-10 bg-[#ccff00]" : "w-4 bg-white/20 hover:bg-white/40"
                }`}
              />
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
